import type { PatternZoneLine } from "@/types/pattern";

export function parseDxfZoneLines(
  text: string,
  options?: {
    bounds?: { minE: number; maxE: number; minN: number; maxN: number };
    minVertices?: number;
  },
): { zoneLines: PatternZoneLine[]; errors: string[] } {
  const errors: string[] = [];
  const zoneLines: PatternZoneLine[] = [];
  const minVerts = options?.minVertices ?? 5;

  if (text.startsWith("AC10") && !text.includes("SECTION")) {
    errors.push("This appears to be a binary DWG file. Please re-export as ASCII DXF.");
    return { zoneLines, errors };
  }

  try {
    const lines = text.split(/\r?\n/);
    const polylines = extractPolylines(lines);

    for (const poly of polylines) {
      if (poly.points.length < minVerts) continue;

      if (options?.bounds) {
        const { minE, maxE, minN, maxN } = options.bounds;
        const xs = poly.points.map((p) => p.easting);
        const ys = poly.points.map((p) => p.northing);
        const polyMinE = Math.min(...xs), polyMaxE = Math.max(...xs);
        const polyMinN = Math.min(...ys), polyMaxN = Math.max(...ys);
        if (polyMaxE < minE || polyMinE > maxE || polyMaxN < minN || polyMinN > maxN) continue;
      }

      const simplified = simplifyPolyline(poly.points, 30);

      zoneLines.push({
        label: poly.layer || "ORE",
        color: "#ff80ff",
        closed: poly.closed,
        points: simplified,
      });
    }
  } catch (err) {
    errors.push(`DXF parse error: ${err instanceof Error ? err.message : String(err)}`);
  }

  return { zoneLines, errors };
}

interface RawPolyline {
  layer: string;
  closed: boolean;
  points: { easting: number; northing: number }[];
}

function extractPolylines(lines: string[]): RawPolyline[] {
  const result: RawPolyline[] = [];
  const pairs: { code: number; value: string }[] = [];
  for (let i = 0; i < lines.length - 1; i += 2) {
    const code = parseInt(lines[i].trim(), 10);
    const value = lines[i + 1]?.trim() ?? "";
    if (!isNaN(code)) pairs.push({ code, value });
  }

  let i = 0;
  while (i < pairs.length) {
    if (pairs[i].code === 0 && pairs[i].value === "LWPOLYLINE") {
      const poly = parseLwPolyline(pairs, i);
      if (poly) result.push(poly.polyline);
      i = poly ? poly.nextIndex : i + 1;
    } else if (pairs[i].code === 0 && pairs[i].value === "POLYLINE") {
      const poly = parsePolyline(pairs, i);
      if (poly) result.push(poly.polyline);
      i = poly ? poly.nextIndex : i + 1;
    } else {
      i++;
    }
  }

  return result;
}

function parseLwPolyline(
  pairs: { code: number; value: string }[],
  start: number,
): { polyline: RawPolyline; nextIndex: number } | null {
  let layer = "0";
  let closed = false;
  const points: { easting: number; northing: number }[] = [];
  let i = start + 1;
  let curX: number | null = null;

  while (i < pairs.length) {
    const { code, value } = pairs[i];
    if (code === 0) break;
    if (code === 8) layer = value;
    if (code === 70) closed = (parseInt(value) & 1) !== 0;
    if (code === 10) curX = parseFloat(value);
    if (code === 20 && curX !== null) {
      points.push({ easting: curX, northing: parseFloat(value) });
      curX = null;
    }
    i++;
  }

  return { polyline: { layer, closed, points }, nextIndex: i };
}

function parsePolyline(
  pairs: { code: number; value: string }[],
  start: number,
): { polyline: RawPolyline; nextIndex: number } | null {
  let layer = "0";
  let closed = false;
  const points: { easting: number; northing: number }[] = [];
  let i = start + 1;

  while (i < pairs.length && pairs[i].code !== 0) {
    if (pairs[i].code === 8) layer = pairs[i].value;
    if (pairs[i].code === 70) closed = (parseInt(pairs[i].value) & 1) !== 0;
    i++;
  }

  while (i < pairs.length) {
    if (pairs[i].code === 0 && pairs[i].value === "SEQEND") { i++; break; }
    if (pairs[i].code === 0 && pairs[i].value === "VERTEX") {
      i++;
      let x: number | null = null;
      let y: number | null = null;
      while (i < pairs.length && pairs[i].code !== 0) {
        if (pairs[i].code === 10) x = parseFloat(pairs[i].value);
        if (pairs[i].code === 20) y = parseFloat(pairs[i].value);
        i++;
      }
      if (x !== null && y !== null) points.push({ easting: x, northing: y });
    } else {
      i++;
    }
  }

  return { polyline: { layer, closed, points }, nextIndex: i };
}

function simplifyPolyline(
  points: { easting: number; northing: number }[],
  target: number,
): { easting: number; northing: number }[] {
  if (points.length <= target) return points;
  const step = Math.max(1, Math.floor(points.length / target));
  const result = [];
  for (let i = 0; i < points.length; i += step) {
    result.push(points[i]);
  }
  const last = points[points.length - 1];
  if (result[result.length - 1] !== last) result.push(last);
  return result;
}
